import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import DomainIcon from "@mui/icons-material/Domain";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Switch,
  Table,
  Textarea,
  Typography
} from "@mui/joy";
import { createRoute, useNavigate } from "@tanstack/react-router";
import React, { useEffect, useState } from "react";
import type { DocumentationType } from "../../shared/api/documentation.service";
import {
  type DomainRequest,
  domainService
} from "../../shared/api/domain.service";
import { useOnboardingStore, useUserStore } from "../../shared/store";
import { DocumentationDialog } from "../../shared/ui/DocumentationDialog";
import { OnboardingChevronStepper } from "../../shared/ui/OnboardingChevronStepper";
import { onboardingRootRoute } from "./onboarding-root";
import { useRoleBasedNavigation } from "../../shared/hook/useRoleBasedNavigation";

// O365 Graph API URL options
const office365GraphURLs = [
  {
    label: "Microsoft Global Service (Default)",
    value: "https://graph.microsoft.com"
  },
  {
    label: "Microsoft for US Government L4 (GovCloud)",
    value: "https://graph.microsoft.us"
  }
];

interface Domain {
  id: string;
  enabled: boolean;
  provider: "google" | "office365";
  type?: "google" | "O365";
  encrypted?: boolean;

  // Google-specific fields
  emailAddress?: string;
  credentials?: string; // JSON service account credentials

  // O365-specific fields
  graphApiUrl?: string;
  useUserPrincipalName?: boolean;
  customIntegrationEnabled?: boolean;
  clientId?: string;
  clientSecret?: string;
  tenantId?: string;
  useRoosterBot?: boolean;
  clientSecretExpiryDate?: string;

  [key: string]: unknown;
}

const DomainPage: React.FC = () => {
  const navigate = useNavigate();
  const { uuid, markStageComplete: markStageCompleteInStore } =
    useOnboardingStore();
  const user = useUserStore((state) => state.user);
  const {
    getNext,
    accessiblePages,
    roles,
    isItAdmin,
    rolesLoaded,
    getStartingPage
  } = useRoleBasedNavigation();

  // Determine the next page label based on user's accessible pages
  const getNextButtonLabel = () => {
    const nextPath = getNext("/onboarding/domain");
    if (!nextPath) return "Complete";

    // Find the page config for the next path
    const nextPage = accessiblePages.find((p) => p.path === nextPath);
    return nextPage?.label || "Next";
  };

  // Redirect users without it-admin role to their appropriate page
  // Guard against empty roles to avoid premature redirect when roles are stale from persisted store
  useEffect(() => {
    if (rolesLoaded && roles.length > 0 && !isItAdmin) {
      const startPage = getStartingPage();
      navigate({ to: startPage });
    }
  }, [rolesLoaded, roles, isItAdmin, navigate, getStartingPage]);

  // Support multiple domains
  const [domains, setDomains] = useState<Domain[]>([]);
  const [newDomainName, setNewDomainName] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingDomainId, setEditingDomainId] = useState<string | null>(null); // Track which domain is being edited
  const [domainType, setDomainType] = useState<"google" | "O365">("google");
  const [_domainProvider, setDomainProvider] = useState<"google" | "O365">(
    "google"
  );
  const [domainEnabled, setDomainEnabled] = useState(true);

  // Google-specific fields
  const [googleEmailAddress, setGoogleEmailAddress] = useState("");
  const [googleServiceAccountToken, setGoogleServiceAccountToken] =
    useState("");

  // O365-specific fields
  const [graphApiUrl, setGraphApiUrl] = useState("https://graph.microsoft.com");
  const [useUserPrincipalName, setUseUserPrincipalName] = useState(false);
  const [_customIntegrationEnabled, setCustomIntegrationEnabled] =
    useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [useRoosterBot, setUseRoosterBot] = useState(false);
  const [clientSecretExpiryDate, setClientSecretExpiryDate] = useState("");

  const [isValidating, _setIsValidating] = useState(false);
  const [error, setError] = useState("");
  const [isCreatingDomains, setIsCreatingDomains] = useState(false);
  const [apiError, setApiError] = useState("");

  // Documentation dialog state
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [documentationType, setDocumentationType] =
    useState<DocumentationType>("google");

  // Track if domains have been persisted to the backend
  // Once saved, domain names cannot be changed
  const [domainsPersistedToBackend, setDomainsPersistedToBackend] =
    useState(false);
  const [tenantIdFromApi, setTenantIdFromApi] = useState<string | null>(null);
  const [isLoadingExistingData, setIsLoadingExistingData] = useState(true);

  // ARCHITECTURE: Load existing data from hub-api (MongoDB) on component mount
  // Do NOT use sessionStorage - MongoDB is the source of truth
  useEffect(() => {
    const loadExistingData = async () => {
      if (!uuid) {
        setIsLoadingExistingData(false);
        return;
      }

      try {
        const { tenantService } = await import(
          "../../shared/api/tenant.service"
        );
        const response = await tenantService.getOnboardingStatus(uuid);

        if (response.success && response.data) {
          // Store tenantId for use in handleNext
          if (response.data.tenantId) {
            setTenantIdFromApi(response.data.tenantId);
          }

          // Load existing domains if available
          // Cast to Domain[] since OnboardingDomainConfig is structurally compatible
          if (
            response.data.onboardingData?.domains &&
            Array.isArray(response.data.onboardingData.domains)
          ) {
            setDomains(response.data.onboardingData.domains as Domain[]);
          }

          // Check if domains have already been saved to the backend
          if (response.data.onboardingData?.domainCreationResponse) {
            setDomainsPersistedToBackend(true);
          }
        }
      } catch {
        // If API call fails, continue with empty form
      } finally {
        setIsLoadingExistingData(false);
      }
    };

    loadExistingData();
  }, [uuid]);

  // Domain validation function
  const validateDomainName = (name: string) => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-._]*[a-zA-Z0-9]$/;
    return domainRegex.test(name) && name.length > 1;
  };

  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // JSON validation function
  const isValidJson = (jsonString: string) => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  };

  const handleCreateDomain = async () => {
    if (!newDomainName.trim()) {
      setError("Domain name is required");
      return;
    }

    if (!validateDomainName(newDomainName)) {
      setError("Please enter a valid domain name");
      return;
    }

    // Check if domain already exists
    if (domains.some((d) => d.id === newDomainName.trim())) {
      setError("Domain already exists");
      return;
    }

    // Validate Google-specific fields
    if (domainType === "google") {
      if (!googleEmailAddress) {
        setError("Email address is required for Google domains");
        return;
      }
      if (!isValidEmail(googleEmailAddress)) {
        setError("Please enter a valid email address");
        return;
      }
      if (!googleServiceAccountToken) {
        setError("JSON credentials are required for Google domains");
        return;
      }
      if (!isValidJson(googleServiceAccountToken)) {
        setError("Please enter valid JSON credentials");
        return;
      }
    }

    // Validate O365-specific fields
    if (domainType === "O365") {
      if (!clientId) {
        setError("Client ID is required for O365 integration");
        return;
      }
      if (!clientSecret) {
        setError("Client Secret is required for O365 integration");
        return;
      }
      if (!tenantId) {
        setError("Tenant ID is required for O365 integration");
        return;
      }
    }

    // Create domain object locally
    const newDomain: Domain = {
      id: newDomainName.trim(),
      enabled: domainEnabled,
      provider: domainType === "google" ? "google" : "office365",
      type: domainType,
      encrypted: false,

      // Add provider-specific fields
      ...(domainType === "google" && {
        emailAddress: googleEmailAddress,
        credentials: googleServiceAccountToken
      }),
      ...(domainType === "O365" && {
        graphApiUrl,
        useUserPrincipalName,
        customIntegrationEnabled: true,
        clientId,
        clientSecret,
        tenantId,
        clientSecretExpiryDate,
        useRoosterBot
      })
    };

    // Add to local domains list
    setDomains((prev) => [...prev, newDomain]);

    // Clear form and close
    setNewDomainName("");
    setDomainType("google");
    setDomainProvider("google");
    setDomainEnabled(true);

    // Clear Google fields
    setGoogleEmailAddress("");
    setGoogleServiceAccountToken("");

    // Clear O365 fields
    setGraphApiUrl("https://graph.microsoft.com");
    setUseUserPrincipalName(false);
    setCustomIntegrationEnabled(domainType === "O365"); // Keep true for O365
    setClientId("");
    setClientSecret("");
    setTenantId("");
    setUseRoosterBot(false);
    setClientSecretExpiryDate("");

    setShowCreateForm(false);
    setError("");
  };

  const handleDeleteDomain = (domainId: string) => {
    setDomains((prev) => prev.filter((d) => d.id !== domainId));
    setError("");
  };

  // Handle editing a domain - populate form with existing data
  const handleEditDomain = (domain: Domain) => {
    setEditingDomainId(domain.id);
    setNewDomainName(domain.id);
    setDomainType(
      domain.type || (domain.provider === "google" ? "google" : "O365")
    );
    setDomainProvider(
      domain.type || (domain.provider === "google" ? "google" : "O365")
    );
    setDomainEnabled(domain.enabled);

    // Populate Google-specific fields
    if (domain.provider === "google") {
      setGoogleEmailAddress(domain.emailAddress || "");
      setGoogleServiceAccountToken(domain.credentials || "");
    }

    // Populate O365-specific fields
    if (domain.provider === "office365") {
      setGraphApiUrl(domain.graphApiUrl || "https://graph.microsoft.com");
      setUseUserPrincipalName(domain.useUserPrincipalName || false);
      setCustomIntegrationEnabled(domain.customIntegrationEnabled !== false);
      setClientId(domain.clientId || "");
      setClientSecret(domain.clientSecret || "");
      setTenantId(domain.tenantId || "");
      setUseRoosterBot(domain.useRoosterBot || false);
      setClientSecretExpiryDate(domain.clientSecretExpiryDate || "");
    }

    setShowCreateForm(true);
    setError("");
  };

  // Handle updating an existing domain
  const handleUpdateDomain = async () => {
    if (!editingDomainId) return;

    // Determine the domain ID to use
    // If domains haven't been persisted to backend yet, allow changing the domain name
    const newDomainId = domainsPersistedToBackend
      ? editingDomainId
      : newDomainName.trim();

    // Validate domain name if it can be changed (not yet persisted)
    if (!domainsPersistedToBackend) {
      if (!newDomainName.trim()) {
        setError("Domain name is required");
        return;
      }
      if (!validateDomainName(newDomainName)) {
        setError("Please enter a valid domain name");
        return;
      }
      // Check for duplicate domain names (excluding the one being edited)
      if (
        newDomainName.trim() !== editingDomainId &&
        domains.some((d) => d.id === newDomainName.trim())
      ) {
        setError("A domain with this name already exists");
        return;
      }
    }

    // Validate Google-specific fields
    if (domainType === "google") {
      if (!googleEmailAddress) {
        setError("Email address is required for Google domains");
        return;
      }
      if (!isValidEmail(googleEmailAddress)) {
        setError("Please enter a valid email address");
        return;
      }
      if (!googleServiceAccountToken) {
        setError("JSON credentials are required for Google domains");
        return;
      }
      if (!isValidJson(googleServiceAccountToken)) {
        setError("Please enter valid JSON credentials");
        return;
      }
    }

    // Validate O365-specific fields
    if (domainType === "O365") {
      if (!clientId) {
        setError("Client ID is required for O365 integration");
        return;
      }
      if (!clientSecret) {
        setError("Client Secret is required for O365 integration");
        return;
      }
      if (!tenantId) {
        setError("Tenant ID is required for O365 integration");
        return;
      }
    }

    // Create updated domain object
    const updatedDomain: Domain = {
      id: newDomainId, // Use new domain name if not persisted, otherwise keep original
      enabled: domainEnabled,
      provider: domainType === "google" ? "google" : "office365",
      type: domainType,
      encrypted: false,

      // Add provider-specific fields
      ...(domainType === "google" && {
        emailAddress: googleEmailAddress,
        credentials: googleServiceAccountToken
      }),
      ...(domainType === "O365" && {
        graphApiUrl,
        useUserPrincipalName,
        customIntegrationEnabled: true,
        clientId,
        clientSecret,
        tenantId,
        clientSecretExpiryDate,
        useRoosterBot
      })
    };

    // Update domain in list
    setDomains((prev) =>
      prev.map((d) => (d.id === editingDomainId ? updatedDomain : d))
    );

    // Reset form and editing state
    resetForm();
  };

  // Helper function to reset form fields
  const resetForm = () => {
    setNewDomainName("");
    setDomainType("google");
    setDomainProvider("google");
    setDomainEnabled(true);
    setEditingDomainId(null);

    // Clear Google fields
    setGoogleEmailAddress("");
    setGoogleServiceAccountToken("");

    // Clear O365 fields
    setGraphApiUrl("https://graph.microsoft.com");
    setUseUserPrincipalName(false);
    setCustomIntegrationEnabled(false);
    setClientId("");
    setClientSecret("");
    setTenantId("");
    setUseRoosterBot(false);
    setClientSecretExpiryDate("");

    setShowCreateForm(false);
    setError("");
  };

  const handleToggleDomain = (domainId: string, enabled: boolean) => {
    setDomains((prev) =>
      prev.map((d) => (d.id === domainId ? { ...d, enabled } : d))
    );
  };

  const handleNext = async () => {
    if (domains.length === 0) {
      setError("At least one domain is required to continue");
      return;
    }

    setIsCreatingDomains(true);
    setApiError("");
    setError("");

    try {
      // ARCHITECTURE: Use tenantId from API (MongoDB) - NOT sessionStorage
      // tenantIdFromApi was loaded from hub-api on component mount
      const tenantId = tenantIdFromApi;

      if (!tenantId) {
        throw new Error(
          "Tenant ID not found. Please complete the workday configuration first."
        );
      }

      // Ensure uuid is available - it's required for looking up the onboarding record
      if (!uuid) {
        setApiError(
          "Session error: Missing onboarding UUID. Please log in again."
        );
        setIsCreatingDomains(false);
        return;
      }

      // ARCHITECTURE: Only send user-entered data to hub-api
      // Hub-api will derive/set: provider, encrypted, customIntegrationEnabled,
      // linkedDomains, adminConsentGranted, adminFlow, isStandalone, etc.
      const domainsForApi: DomainRequest[] = domains.map((domain) => {
        // Common user-entered fields
        const baseFields = {
          id: domain.id,
          enabled: domain.enabled,
          type: domain.type // Hub-api derives 'provider' from 'type'
        };

        // Google-specific user-entered fields
        if (domain.type === "google") {
          return {
            ...baseFields,
            provider: "google" as const,
            ...(domain.emailAddress && { emailAddress: domain.emailAddress }),
            ...(domain.credentials && { credentials: domain.credentials })
          };
        }

        // O365-specific user-entered fields
        return {
          ...baseFields,
          provider: "office365" as const,
          ...(domain.clientId && { clientId: domain.clientId }),
          ...(domain.clientSecret && { clientSecret: domain.clientSecret }),
          ...(domain.tenantId && { tenantId: domain.tenantId }),
          ...(domain.graphApiUrl && { graphApiUrl: domain.graphApiUrl }),
          ...(domain.useUserPrincipalName !== undefined && {
            useUserPrincipalName: domain.useUserPrincipalName
          }),
          ...(domain.useRoosterBot !== undefined && {
            useRoosterBot: domain.useRoosterBot
          }),
          ...(domain.clientSecretExpiryDate && {
            clientSecretExpiryDate: domain.clientSecretExpiryDate
          })
        };
      });

      // Call API to create domain documents
      // Pass uuid so backend can determine correct environment from MongoDB
      const response = await domainService.addDomains(tenantId, {
        domains: domainsForApi,
        uuid: uuid
      });

      // ARCHITECTURE: Do NOT store in sessionStorage - only send to API
      // The API call saves to MongoDB, which is the source of truth
      const domainOnboardingData = {
        domains: domains,
        domainCreationResponse: response
      };

      // Update onboarding stage to DOMAIN_CONFIGURED
      const { tenantService } = await import("../../shared/api/tenant.service");
      await tenantService.updateOnboardingStage(tenantId, {
        uuid: uuid,
        stage: "DOMAIN_CONFIGURED",
        tenantId: tenantId,
        onboardingData: domainOnboardingData
      });

      // Mark domain stage as complete in the sequenced workflow
      try {
        const userEmail = user?.email || "unknown";
        await tenantService.markStageComplete(uuid, "domain", userEmail);
        // Also update local store
        markStageCompleteInStore("domain", userEmail);
      } catch (stageError) {
        console.error("Error marking domain stage complete:", stageError);
        // Don't fail the navigation if stage marking fails
      }

      // Navigate to next page based on user roles
      const nextPage = getNext("/onboarding/domain");
      navigate({ to: nextPage || "/onboarding/completion" });
    } catch (err: unknown) {
      console.error("Error creating domains:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to create domain documents. Please try again.";
      setApiError(errorMessage);
    } finally {
      setIsCreatingDomains(false);
    }
  };

  const handleBack = () => {
    navigate({ to: "/onboarding/workday" });
  };

  const openDocumentation = () => {
    // Set the documentation type based on currently selected domain type
    setDocumentationType(domainType === "google" ? "google" : "office365");
    setShowDocumentation(true);
  };

  const isNextDisabled =
    domains.length === 0 || isValidating || isCreatingDomains;

  return (
    <Container maxWidth="lg">
      <OnboardingChevronStepper currentStep={2} />
      <Box sx={{ py: 4 }}>
        <Typography
          level="h2"
          sx={{ mb: 3, textAlign: "center" }}
          startDecorator={<DomainIcon />}
        >
          Domain Configuration
        </Typography>

        <Alert color="primary" sx={{ mb: 3 }}>
          <Typography level="body-sm">
            Configure domain settings for your Rooster environment. You can add
            multiple domains and manage their settings.
          </Typography>
        </Alert>

        {error && (
          <Alert
            color="danger"
            variant="soft"
            startDecorator={<ErrorIcon />}
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        )}

        {apiError && (
          <Alert
            color="danger"
            variant="soft"
            startDecorator={<ErrorIcon />}
            sx={{ mb: 3 }}
          >
            <Typography level="title-sm">Domain Creation Error</Typography>
            <Typography level="body-sm">{apiError}</Typography>
          </Alert>
        )}

        {/* Create Domain Section */}
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2
            }}
          >
            <Typography level="title-md">Domains</Typography>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<AddIcon />}
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              Add Domain
            </Button>
          </Box>

          {showCreateForm && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid xs={12} md={8}>
                <Sheet variant="outlined" sx={{ borderRadius: "sm", p: 3 }}>
                  <Typography level="title-sm" sx={{ mb: 2 }}>
                    {editingDomainId ? "Edit Domain" : "Create New Domain"}
                  </Typography>

                  <Stack spacing={2}>
                    <FormControl>
                      <FormLabel>Domain Name *</FormLabel>
                      <Input
                        value={newDomainName}
                        onChange={(e) => {
                          setNewDomainName(e.target.value);
                          setError("");
                        }}
                        placeholder="example.com"
                        error={
                          !!(
                            newDomainName && !validateDomainName(newDomainName)
                          )
                        }
                        disabled={
                          !!editingDomainId && domainsPersistedToBackend
                        } // Domain name can only be changed before saving to backend
                      />
                      {editingDomainId && domainsPersistedToBackend && (
                        <Typography level="body-xs" color="neutral">
                          Domain name cannot be changed after saving
                        </Typography>
                      )}
                      {newDomainName && !validateDomainName(newDomainName) && (
                        <Typography level="body-xs" color="danger">
                          Please enter a valid domain name
                        </Typography>
                      )}
                    </FormControl>

                    <FormControl>
                      <FormLabel>Domain Type</FormLabel>
                      <Select
                        value={domainType}
                        onChange={(_, value) => {
                          const newType = value as "google" | "O365";
                          setDomainType(newType);
                          setDomainProvider(newType);
                          // Set customIntegrationEnabled to true for O365
                          if (newType === "O365") {
                            setCustomIntegrationEnabled(true);
                          }
                        }}
                      >
                        <Option value="google">Google</Option>
                        <Option value="O365">Office 365</Option>
                      </Select>
                    </FormControl>

                    {/* Google-specific fields */}
                    {domainType === "google" && (
                      <>
                        <Typography level="body-md" sx={{ mt: 2, mb: 1 }}>
                          Please provide the email address of your domain
                          administrator. Rooster needs this information to
                          access and fetch calendar events for users in your
                          domain.
                        </Typography>

                        <FormControl>
                          <FormLabel>Email Address *</FormLabel>
                          <Input
                            value={googleEmailAddress}
                            onChange={(e) => {
                              setGoogleEmailAddress(e.target.value);
                              setError("");
                            }}
                            placeholder="admin@example.com"
                            error={
                              !!(
                                googleEmailAddress &&
                                !isValidEmail(googleEmailAddress)
                              )
                            }
                          />
                          {googleEmailAddress &&
                            !isValidEmail(googleEmailAddress) && (
                              <Typography level="body-xs" color="danger">
                                Please enter a valid email address
                              </Typography>
                            )}
                        </FormControl>

                        <Typography level="body-md">
                          Paste in Google JSON Service Account Credentials
                        </Typography>

                        <FormControl>
                          <FormLabel>JSON Credentials *</FormLabel>
                          <Textarea
                            value={googleServiceAccountToken}
                            onChange={(e) => {
                              setGoogleServiceAccountToken(e.target.value);
                              setError("");
                            }}
                            placeholder="Paste your Google JSON service account credentials here"
                            minRows={10}
                            maxRows={15}
                            error={
                              !!(
                                googleServiceAccountToken &&
                                !isValidJson(googleServiceAccountToken)
                              )
                            }
                          />
                          {googleServiceAccountToken &&
                            !isValidJson(googleServiceAccountToken) && (
                              <Typography level="body-xs" color="danger">
                                Please enter valid JSON format
                              </Typography>
                            )}
                        </FormControl>
                      </>
                    )}

                    {/* O365-specific fields */}
                    {domainType === "O365" && (
                      <>
                        <FormControl>
                          <FormLabel>Graph URL</FormLabel>
                          <Select
                            value={graphApiUrl}
                            onChange={(_, value) =>
                              setGraphApiUrl(
                                value || "https://graph.microsoft.com"
                              )
                            }
                          >
                            {office365GraphURLs.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        </FormControl>

                        <Stack direction="row" spacing={2} alignItems="center">
                          <FormControl
                            orientation="horizontal"
                            sx={{ flex: 1 }}
                          >
                            <Switch
                              checked={useUserPrincipalName}
                              onChange={(e) =>
                                setUseUserPrincipalName(e.target.checked)
                              }
                            />
                            <FormLabel>
                              Use userPrincipalName as Email
                            </FormLabel>
                          </FormControl>

                          <FormControl
                            orientation="horizontal"
                            sx={{ flex: 1 }}
                          >
                            <Switch
                              checked={useRoosterBot}
                              onChange={(e) =>
                                setUseRoosterBot(e.target.checked)
                              }
                            />
                            <FormLabel>Use RoosterBot</FormLabel>
                          </FormControl>
                        </Stack>

                        <Stack spacing={2}>
                          <Typography level="body-md">
                            Custom O365 Integration Settings
                          </Typography>

                          <FormControl>
                            <FormLabel>Client ID *</FormLabel>
                            <Input
                              value={clientId}
                              onChange={(e) => {
                                setClientId(e.target.value);
                                setError("");
                              }}
                              placeholder="Enter Client ID"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Client Secret *</FormLabel>
                            <Input
                              value={clientSecret}
                              onChange={(e) => {
                                setClientSecret(e.target.value);
                                setError("");
                              }}
                              placeholder="Enter Client Secret"
                              type="password"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Tenant ID *</FormLabel>
                            <Input
                              value={tenantId}
                              onChange={(e) => {
                                setTenantId(e.target.value);
                                setError("");
                              }}
                              placeholder="Enter Tenant ID"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Client Secret Expiry Date</FormLabel>
                            <Input
                              value={clientSecretExpiryDate}
                              onChange={(e) =>
                                setClientSecretExpiryDate(e.target.value)
                              }
                              placeholder="YYYY-MM-DD"
                              type="date"
                            />
                          </FormControl>
                        </Stack>
                      </>
                    )}

                    <FormControl orientation="horizontal">
                      <Switch
                        checked={domainEnabled}
                        onChange={(e) => setDomainEnabled(e.target.checked)}
                      />
                      <FormLabel>Enable Domain</FormLabel>
                    </FormControl>

                    <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                      <Button variant="outlined" onClick={resetForm}>
                        Cancel
                      </Button>
                      <Button
                        onClick={
                          editingDomainId
                            ? handleUpdateDomain
                            : handleCreateDomain
                        }
                      >
                        {editingDomainId ? "Update Domain" : "Create Domain"}
                      </Button>
                    </Stack>
                  </Stack>
                </Sheet>
              </Grid>

              {/* Help Box */}
              <Grid xs={12} md={4}>
                <Card
                  variant="soft"
                  color="primary"
                  sx={{ height: "100%", position: "sticky", top: 20 }}
                >
                  <CardContent>
                    <Stack spacing={2}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <InfoOutlinedIcon />
                        <Typography level="title-md">Setup Guide</Typography>
                      </Box>

                      {domainType === "google" && (
                        <Stack spacing={1.5}>
                          <Typography level="title-sm">
                            Google Domain Setup
                          </Typography>
                          <Typography level="body-sm">
                            To configure a Google domain, you'll need:
                          </Typography>
                          <Box component="ol" sx={{ pl: 2, m: 0 }}>
                            <Box component="li" sx={{ mb: 1 }}>
                              <Typography level="body-sm">
                                <strong>Domain Name:</strong> Your
                                organization's domain (e.g., company.com)
                              </Typography>
                            </Box>
                            <Box component="li" sx={{ mb: 1 }}>
                              <Typography level="body-sm">
                                <strong>Admin Email:</strong> A Google Workspace
                                admin email address that Rooster will use to
                                access calendar data
                              </Typography>
                            </Box>
                            <Box component="li">
                              <Typography level="body-sm">
                                <strong>Service Account Credentials:</strong>{" "}
                                JSON credentials file from Google Cloud Console
                                with domain-wide delegation enabled
                              </Typography>
                            </Box>
                          </Box>
                          <Alert variant="soft" color="neutral" sx={{ mt: 1 }}>
                            <Typography level="body-xs">
                              The service account must have domain-wide
                              delegation with Calendar API access to read user
                              calendars.
                            </Typography>
                          </Alert>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="sm"
                            startDecorator={<MenuBookIcon />}
                            onClick={openDocumentation}
                            sx={{ mt: 2 }}
                          >
                            View Documentation
                          </Button>
                        </Stack>
                      )}

                      {domainType === "O365" && (
                        <Stack spacing={1.5}>
                          <Typography level="title-sm">
                            Office 365 Domain Setup
                          </Typography>
                          <Typography level="body-sm">
                            To configure an O365 domain, you'll need to register
                            an app in Azure AD and provide:
                          </Typography>
                          <Box component="ol" sx={{ pl: 2, m: 0 }}>
                            <Box component="li" sx={{ mb: 1 }}>
                              <Typography level="body-sm">
                                <strong>Domain Name:</strong> Your
                                organization's domain (e.g., company.com)
                              </Typography>
                            </Box>
                            <Box component="li" sx={{ mb: 1 }}>
                              <Typography level="body-sm">
                                <strong>Client ID:</strong> Application (client)
                                ID from your Azure AD app registration
                              </Typography>
                            </Box>
                            <Box component="li" sx={{ mb: 1 }}>
                              <Typography level="body-sm">
                                <strong>Client Secret:</strong> A secret value
                                generated for your app in Azure AD
                              </Typography>
                            </Box>
                            <Box component="li" sx={{ mb: 1 }}>
                              <Typography level="body-sm">
                                <strong>Tenant ID:</strong> Your Azure AD
                                Directory (tenant) ID
                              </Typography>
                            </Box>
                            <Box component="li">
                              <Typography level="body-sm">
                                <strong>Graph API URL:</strong> Select the
                                appropriate endpoint for your environment
                              </Typography>
                            </Box>
                          </Box>
                          <Alert variant="soft" color="neutral" sx={{ mt: 1 }}>
                            <Typography level="body-xs">
                              Your app must have Microsoft Graph API permissions
                              for Calendars.Read and User.Read.All with admin
                              consent granted.
                            </Typography>
                          </Alert>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="sm"
                            startDecorator={<MenuBookIcon />}
                            onClick={openDocumentation}
                            sx={{ mt: 2 }}
                          >
                            View Documentation
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Domains List */}
          {domains.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography level="title-sm" sx={{ mb: 2 }}>
                  Configured Domains ({domains.length})
                </Typography>
                <Table>
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domains.map((domain) => (
                      <tr key={domain.id}>
                        <td>
                          <Typography level="body-sm" fontWeight="md">
                            {domain.id}
                          </Typography>
                        </td>
                        <td>
                          <Chip
                            variant="soft"
                            color={
                              domain.type === "google" ? "success" : "primary"
                            }
                          >
                            {domain.type === "google" ? "Google" : "Office 365"}
                          </Chip>
                        </td>
                        <td>
                          <Chip
                            variant="soft"
                            color={domain.enabled ? "success" : "neutral"}
                            startDecorator={
                              domain.enabled ? <CheckCircleIcon /> : undefined
                            }
                          >
                            {domain.enabled ? "Enabled" : "Disabled"}
                          </Chip>
                        </td>
                        <td>
                          <Stack direction="row" spacing={1}>
                            <Switch
                              checked={domain.enabled}
                              onChange={(e) =>
                                handleToggleDomain(domain.id, e.target.checked)
                              }
                              size="sm"
                            />
                            <IconButton
                              variant="outlined"
                              color="primary"
                              size="sm"
                              onClick={() => handleEditDomain(domain)}
                              title="Edit domain"
                              aria-label="Edit domain"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              variant="outlined"
                              color="danger"
                              size="sm"
                              onClick={() => handleDeleteDomain(domain.id)}
                              title="Delete domain"
                              aria-label="Delete domain"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardContent>
            </Card>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button variant="outlined" onClick={handleBack} size="lg">
            Back
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleNext}
            disabled={isNextDisabled}
            loading={isCreatingDomains}
            size="lg"
          >
            {isCreatingDomains
              ? "Creating Domains..."
              : `Next: ${getNextButtonLabel()}`}
          </Button>
        </Stack>
      </Box>

      {/* Documentation Dialog */}
      <DocumentationDialog
        open={showDocumentation}
        onClose={() => setShowDocumentation(false)}
        docType={documentationType}
      />
    </Container>
  );
};

export const domainRoute = createRoute({
  getParentRoute: () => onboardingRootRoute,
  path: "/domain",
  component: DomainPage
});
